import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Shared PageHeader component for consistent page headers across the app.
 * Uses MUI theme typography variants with consistent styling.
 * 
 * @param {string} title - Page title (required)
 * @param {string} subtitle - Optional subtitle text
 * @param {React.ReactNode} actions - Optional action buttons (e.g., Refresh, Create)
 * @param {React.ReactNode} icon - Optional icon to display next to title
 * @param {string} size - 'default' | 'large' - defaults to 'default' (1.75rem/2rem), large is 2rem/2.5rem
 * @param {object} sx - Additional sx styles for the container
 */
const PageHeader = ({ 
  title, 
  subtitle, 
  actions, 
  icon,
  size = 'default',
  sx = {}
}) => {
  const titleSize = size === 'large' 
    ? { xs: '2rem', md: '2.5rem' } 
    : { xs: '1.75rem', md: '2rem' };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        mb: 4,
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {icon && (
          <Box sx={{ color: 'var(--dwlf-accent)', fontSize: '1.5rem', display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <Box>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: titleSize,
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: 'var(--dwlf-text)',
              mb: subtitle ? 0.5 : 0,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              component="div"
              sx={{ 
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.85rem',
                color: 'var(--dwlf-ink-50)',
                letterSpacing: '0.01em',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
